import type React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";

import type { IHomeClubCardInfo } from "@/api/interfaces/apolloClubCardLayout";
import MyGiftCardIcon from "@/assets/svg/gift_cards_icon.svg?react";
import PlusIcon from "@/assets/svg/orange_plus.svg?react";
import { INTRO_BLOCK_ID } from "@/base/blocks/IntroductionBlocks";
import { PERKS_BLOCK_ID } from "@/base/blocks/PerkBlock/PerkBlock";
import { Button, ButtonTile, P } from "@/base/components/Global";
import Icon from "@/base/components/Global/Icon";
import { Col, Row } from "@/base/components/Layout";
import { useMediaQuery } from "@/base/components/MediaQueryProvider";
import { getApolloLogoWithStartURLByLanguage } from "@/base/utils/iconUtil";
import { scrollToWithOffset } from "@/base/utils/scrollHelpers";
import { navigateTo } from "@/base/utils/uriHelper";
import { APOLLO_CLUB_CARD_BG } from "@/constants/blobIcons";
import config from "@/constants/config";
import { NavigationPath } from "@/constants/navigation";
import { useAppSelector } from "@/store";
import { selectCurrentLanguage } from "@/store/language";
import { selectCurrentRegionPrefix } from "@/store/region";

type ApolloClubCardProps = {
  block?: IHomeClubCardInfo;
};

const ApolloClubCard: React.FC<ApolloClubCardProps> = ({ block }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [{ isCurrentSizeSm }] = useMediaQuery();
  const currentLanguage = useAppSelector(selectCurrentLanguage);
  const currentRegionPrefix = useAppSelector(selectCurrentRegionPrefix);

  const scrollToIntroductionBlock = () => {
    if (isAuthenticated) {
      void navigate(NavigationPath.ApolloClubPromo);
    } else {
      scrollToWithOffset(INTRO_BLOCK_ID);
    }
  };

  const onClickGiftCards = () => {
    if (isAuthenticated) {
      void navigate(NavigationPath.GiftCard);
    } else {
      scrollToWithOffset(PERKS_BLOCK_ID);
    }
  };

  const navigateToSignUp = () =>
    navigateTo(
      config.routerConfig.loyaltyProfileUrl,
      NavigationPath.SignUpStart,
      currentLanguage,
      currentRegionPrefix,
    );

  if (!block) return null;

  return (
    <Row>
      <Col col={12} colMd={6} alignCenter>
        <Icon icon={APOLLO_CLUB_CARD_BG} />
      </Col>
      <Col col={12} colMd={6} directionColumn noMarginRight={isCurrentSizeSm}>
        <Row noMargin>
          <Col col={6} noMarginTop noMarginLeft={!isCurrentSizeSm}>
            <ButtonTile icon={<MyGiftCardIcon />} action={onClickGiftCards}>
              {t("my_gift_cards")}
            </ButtonTile>
          </Col>
          <Col col={6} noMarginTop noMarginRight={!isCurrentSizeSm}>
            <ButtonTile
              icon={
                <Icon
                  icon={getApolloLogoWithStartURLByLanguage(i18n.language)}
                  height="90"
                />
              }
              action={scrollToIntroductionBlock}
            />
          </Col>
          {block?.cta?.label && (
            <Col col={12} alignCenter noMarginBottom>
              <Button
                prefixIcon={<PlusIcon />}
                transparentBrand
                action={navigateToSignUp}
              >
                {block.cta.label}
              </Button>
            </Col>
          )}
        </Row>
        <Col col={12} noHorizontalMargin={!isCurrentSizeSm}>
          <P noMargin cmsContent={block.description} />
        </Col>
      </Col>
    </Row>
  );
};

export default ApolloClubCard;
